import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2 } from 'lucide-react';
import type { AxiosError } from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useUpdateProfile } from '../hooks/mutations/useUpdateProfile';
import { useDeleteAccount } from '../hooks/mutations/useDeleteAccount';
import { useMyCodes } from '../hooks/queries/useMyCodes';
import { useRedeemCode } from '../hooks/mutations/useRedeemCode';
import { Card, Input, PasswordInput, Button, Badge } from '../components/ui';
import DeleteAccountModal from '../components/DeleteAccountModal';
import ConfirmDialog from '../components/ConfirmDialog';
import RedemptionTerms from '../components/RedemptionTerms';
import { linkButtonClass } from '../utils/linkButtonClass';
import { formatGrantParts, formatMonthDay, formatRedemptionCode, formatUsageHours, nextRenewalLabel, planLabel } from '../utils/formatters';
import { extraSessionsNote } from '../utils/sessionQuota';
import Layout from '../components/Layout';
import Header from '../components/Header';
import { BILLING_ENABLED } from '../config/featureFlags';
import type { User } from '../types';

interface ProfilePageProps {
  user: User;
}

// プラン情報に添える期間の表示。契約中は次回更新日、解約予約中は満了日。
// 無料プラン・日付未取得なら null（行ごと出さない）
function planPeriodNote(plan: User['plan']): string | null {
  const renewal = nextRenewalLabel(plan);
  if (renewal) return renewal;
  if (plan.plan_type !== 'free' && plan.cancel_at_period_end && plan.cancel_effective_at) {
    return `${formatMonthDay(plan.cancel_effective_at)}まで利用可`;
  }
  return null;
}

const REDEEM_FALLBACK_MESSAGE = '引き換えに失敗しました。時間をおいて再度お試しください。';

export default function ProfilePage({ user }: ProfilePageProps) {
  const { logout, updatePassword, currentUser } = useAuth();
  const navigate = useNavigate();

  // メール・パスワードで登録したユーザーのみパスワード変更が可能。
  // ソーシャルログイン（OAuth）のみのユーザーはパスワードを持たないため導線を隠す。
  // identities に provider==='email' が含まれるかで判定する（アカウント連携も考慮）。
  const canChangePassword =
    currentUser?.identities?.some((identity) => identity.provider === 'email') ?? false;
  // 新規ユーザーは handle_new_user トリガで display_name=email が既定値として入る。
  // 未設定（=メールと同一）はプレースホルダー案内のため空欄扱いにする。
  const [displayName, setDisplayName] = useState(
    user.display_name && user.display_name !== user.email ? user.display_name : '',
  );
  const [message, setMessage] = useState('');
  // 成功/失敗で枠色を分けるための種別（文言は現行どおり不変）
  const [messageType, setMessageType] = useState<'success' | 'error' | ''>('');
  // 成功時に AuthContext を再取得して user を即時反映（Hook の onSuccess）
  const updateProfile = useUpdateProfile();

  // アカウント削除（退会）はアカウント管理の操作としてこのプロフィール最下部に配置する
  const deleteAccount = useDeleteAccount();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleDeleteAccount = async (reason: string, feedback: string) => {
    await deleteAccount.mutateAsync({ reason, feedback });
    navigate('/');
  };

  // パスワード変更フォームの開閉と入力状態（メール・パスワード登録ユーザー向け）
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordMessageType, setPasswordMessageType] = useState<'success' | 'error' | ''>('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  const resetPasswordForm = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleCancelPasswordChange = () => {
    setShowPasswordForm(false);
    resetPasswordForm();
    setPasswordMessage('');
    setPasswordMessageType('');
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordMessage('');
    setPasswordMessageType('');

    if (newPassword.length < 6) {
      setPasswordMessage('新しいパスワードは6文字以上で入力してください');
      setPasswordMessageType('error');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordMessage('新しいパスワードが一致しません');
      setPasswordMessageType('error');
      return;
    }

    setPasswordLoading(true);
    try {
      await updatePassword(currentPassword, newPassword);
      // 成功時はフォームを畳み、案内メッセージを残す（入力値はクリア）。
      setShowPasswordForm(false);
      resetPasswordForm();
      setPasswordMessage('パスワードを変更しました');
      setPasswordMessageType('success');
    } catch (err: any) {
      const message = err?.message?.includes('Invalid login credentials')
        ? '現在のパスワードが正しくありません'
        : err?.message?.includes('should be different')
        ? '新しいパスワードは現在のパスワードと異なるものを設定してください'
        : err?.message?.includes('at least 6 characters')
        ? '新しいパスワードは6文字以上で入力してください'
        : 'パスワードの変更に失敗しました';
      setPasswordMessage(message);
      setPasswordMessageType('error');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleUpdateProfile = (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');
    setMessageType('');
    updateProfile.mutate(displayName, {
      onSuccess: () => {
        setMessage('プロフィールを更新しました');
        setMessageType('success');
      },
      onError: () => {
        setMessage('更新に失敗しました');
        setMessageType('error');
      },
    });
  };

  // 特典コード（500人記念キャンペーン）。譲渡された人も同じ画面で引き換えるため、
  // 発行されていない利用者にもセクション自体は表示する（空の状態を出し、エラーにしない）。
  const myCodes = useMyCodes();
  const redeemCode = useRedeemCode();
  const [codeInput, setCodeInput] = useState('');
  const [redeemMessage, setRedeemMessage] = useState('');
  const [redeemMessageType, setRedeemMessageType] = useState<'success' | 'error' | ''>('');
  // コピー失敗は引き換えとは別 state に持つ。共有すると互いのメッセージを消し合ううえ、
  // 表示位置も一覧セクションと引き換えセクションで食い違う。
  const [copyError, setCopyError] = useState('');
  // コピー成功はコードごとに持つ。一覧に複数行あるため、どの行を押したのかが分からないと
  // フィードバックにならない（トースト基盤が無いのでボタン内表示で代替する）
  const [copiedCode, setCopiedCode] = useState('');

  const handleRedeem = (e: React.FormEvent) => {
    e.preventDefault();
    setRedeemMessage('');
    setRedeemMessageType('');
    // 送るのは入力そのまま（trim のみ）。ハイフン除去・大文字化は DB 関数の責務。
    redeemCode.mutate(codeInput.trim(), {
      onSuccess: ({ granted_hours, granted_sessions }) => {
        // 文言は付与量から組み立てる（一覧と同じ formatGrantParts で表記をそろえる）。
        const hasHours = granted_hours > 0;
        const hasSessions = granted_sessions > 0;
        const increased = hasHours && hasSessions ? '利用時間と回数' : hasSessions ? '書き起こし回数' : '利用時間';
        setRedeemMessage(
          `${formatGrantParts(granted_hours, granted_sessions).join('と')}を追加しました。増えるのは${increased}のみで、話者分離などのプラン機能は変わりません。`,
        );
        setRedeemMessageType('success');
        setCodeInput('');
      },
      onError: (err) => {
        const error = err as AxiosError<{ detail?: unknown }>;
        // 401 の detail は英語の内部向け文言（"Invalid or expired token"）なので、
        // 文字列であってもそのまま出さない。ステータスで先に分岐する。
        if (error?.response?.status === 401) {
          setRedeemMessage('セッションの有効期限が切れました。再度ログインしてください。');
          setRedeemMessageType('error');
          return;
        }
        // 422（detail がオブジェクトの配列）をそのまま描画すると画面全体が落ちるため型で守る。
        const detail = error?.response?.data?.detail;
        setRedeemMessage(typeof detail === 'string' ? detail : REDEEM_FALLBACK_MESSAGE);
        setRedeemMessageType('error');
      },
    });
  };

  const handleCopyCode = async (code: string) => {
    setCopyError('');
    try {
      await navigator.clipboard.writeText(formatRedemptionCode(code));
      setCopiedCode(code);
      // 一定時間で通常表示へ戻す（TranscriptionPage の copyTranscript と同じ扱い）
      setTimeout(() => setCopiedCode(''), 2000);
    } catch {
      // 手で控えるしかないコードで無反応にはしない（同ページの copyTranscript とは扱いを変える）
      setCopiedCode('');
      setCopyError('コピーできませんでした。表示されているコードを手入力してください。');
    }
  };

  // ログアウトは確認を挟む（誤クリックで作業中の画面から飛ばされるのを防ぐ）
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <Layout>
      <Header user={user} />

      <main className="max-w-2xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">プロフィール</h1>

        <Card className="mb-6">
          <h2 className="text-xl font-bold mb-4">基本情報</h2>
          <form onSubmit={handleUpdateProfile} className="space-y-4">
            <div>
              <label htmlFor="profile-email" className="block text-sm font-medium mb-2">メールアドレス</label>
              <Input
                id="profile-email"
                type="email"
                value={user.email}
                disabled
              />
            </div>

            <div>
              <label htmlFor="profile-display-name" className="block text-sm font-medium mb-2">表示名</label>
              <Input
                id="profile-display-name"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="例: ○○さん"
              />
            </div>

            {message && (
              <div
                className={
                  messageType === 'error'
                    ? 'bg-danger-bg text-danger-fg px-4 py-2 rounded-field'
                    : 'bg-success-bg text-success-fg px-4 py-2 rounded-field'
                }
              >
                {message}
              </div>
            )}

            <Button type="submit" loading={updateProfile.isPending}>
              {updateProfile.isPending ? '更新中...' : '更新'}
            </Button>
          </form>

          {/* パスワード変更（メール・パスワード登録ユーザーのみ表示） */}
          {canChangePassword && (
          <div className="mt-6 pt-6 border-t">
            <h3 className="text-base font-medium mb-4">パスワード</h3>

            {showPasswordForm ? (
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div>
                  <label htmlFor="current-password" className="block text-sm font-medium mb-2">現在のパスワード</label>
                  <PasswordInput
                    id="current-password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    autoComplete="current-password"
                    placeholder="現在のパスワードを入力"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="new-password" className="block text-sm font-medium mb-2">新しいパスワード</label>
                  <PasswordInput
                    id="new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    autoComplete="new-password"
                    placeholder="6文字以上で入力"
                    minLength={6}
                    required
                  />
                </div>
                <div>
                  <label htmlFor="confirm-password" className="block text-sm font-medium mb-2">新しいパスワード（確認）</label>
                  <PasswordInput
                    id="confirm-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                    placeholder="新しいパスワードを再入力"
                    minLength={6}
                    required
                  />
                </div>

                {passwordMessage && passwordMessageType === 'error' && (
                  <div role="alert" className="bg-danger-bg text-danger-fg px-4 py-2 rounded-field">
                    {passwordMessage}
                  </div>
                )}

                <div className="flex gap-2">
                  <Button type="submit" loading={passwordLoading}>
                    {passwordLoading ? '変更中...' : 'パスワードを変更'}
                  </Button>
                  <Button type="button" variant="secondary" onClick={handleCancelPasswordChange} disabled={passwordLoading}>
                    キャンセル
                  </Button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                {passwordMessage && passwordMessageType === 'success' && (
                  <div role="status" className="bg-success-bg text-success-fg px-4 py-2 rounded-field">
                    {passwordMessage}
                  </div>
                )}
                <Button type="button" variant="secondary" onClick={() => setShowPasswordForm(true)}>
                  パスワードを変更する
                </Button>
              </div>
            )}
          </div>
          )}
        </Card>

        <Card className="mb-6">
          <h2 className="text-xl font-bold mb-4">プラン情報</h2>
          <div className="space-y-4">
            <div>
              <div className="text-sm text-gray-600">現在のプラン</div>
              <div className="text-2xl font-bold text-primary">
                {planLabel(user.plan.plan_type)}
              </div>
              {planPeriodNote(user.plan) && (
                <div data-testid="profile-plan-period" className="text-sm text-gray-600 mt-1">
                  {planPeriodNote(user.plan)}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-gray-600">今月の使用セッション</div>
                <div className="font-bold">
                  {/* -1 は「無制限」センチネル。生の -1 を表示しない */}
                  {user.plan.sessions_used} / {user.plan.sessions_limit === -1 ? '無制限' : user.plan.sessions_limit}
                  {extraSessionsNote(user.plan)}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-600">今月の使用時間</div>
                <div className="font-bold">
                  {/* 表記はダッシュボードと共通（formatUsageHours）。-1 は「無制限」センチネル */}
                  {formatUsageHours(user.plan.hours_used)} /{' '}
                  {formatUsageHours(
                    user.plan.hours_limit === -1
                      ? -1
                      : user.plan.hours_limit + (user.plan.extra_hours ?? 0),
                  )}
                </div>
              </div>
            </div>
            {BILLING_ENABLED && (
              <Link to="/billing" className={linkButtonClass('secondary', 'w-full block text-center')}>
                プランを変更
              </Link>
            )}
          </div>
        </Card>

        {/* 特典コード — 引き換えで増えるのは「プラン情報」の利用時間なので、その直後に置く */}
        <Card className="mb-6">
          <h2 className="text-xl font-bold mb-4">特典コード</h2>

          <div className="mb-6">
            <h3 className="text-base font-medium mb-3">あなたのコード</h3>
            {/* 空の状態は data の実在で判定する。isLoading（= isPending && isFetching）は
                オフライン等で fetchStatus が paused になると false のままなので、それを
                条件にすると「取得できていない」を「コードが無い」と断定表示してしまう */}
            {myCodes.isPending ? (
              <p className="text-sm text-gray-600">読み込み中...</p>
            ) : myCodes.isError ? (
              <div className="space-y-3">
                <div role="alert" className="bg-danger-bg text-danger-fg px-4 py-2 rounded-field">
                  コードの取得に失敗しました。
                </div>
                <Button type="button" variant="secondary" onClick={() => myCodes.refetch()}>
                  再試行
                </Button>
              </div>
            ) : myCodes.data && myCodes.data.length > 0 ? (
              <ul className="space-y-3">
                {myCodes.data.map((item) => (
                  <li
                    key={item.code}
                    className="flex flex-wrap items-center gap-3 border rounded-field px-4 py-3"
                  >
                    <span className="font-mono">{formatRedemptionCode(item.code)}</span>
                    <span className="text-sm text-gray-600">
                      +{formatGrantParts(item.grant_hours, item.grant_sessions).join('・')}
                    </span>
                    {/* 使用状態を色だけで表さず、テキストを併記する */}
                    {item.redeemed_at ? (
                      <Badge>使用済み</Badge>
                    ) : (
                      <Button
                        type="button"
                        variant="secondary"
                        aria-label={`${formatRedemptionCode(item.code)} をコピー`}
                        onClick={() => handleCopyCode(item.code)}
                      >
                        {copiedCode === item.code ? 'コピーしました' : 'コピー'}
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-600">発行されたコードはありません。</p>
            )}

            {copyError && (
              <div role="alert" className="mt-3 bg-danger-bg text-danger-fg px-4 py-2 rounded-field">
                {copyError}
              </div>
            )}
          </div>

          <div className="mb-6 pt-6 border-t">
            <h3 className="text-base font-medium mb-3">コードを使う</h3>
            <form onSubmit={handleRedeem} className="space-y-4">
              <div>
                <label htmlFor="redeem-code" className="block text-sm font-medium mb-2">
                  特典コード
                </label>
                <Input
                  id="redeem-code"
                  type="text"
                  value={codeInput}
                  onChange={(e) => setCodeInput(e.target.value)}
                  placeholder="例: A1B2-C3D4-E5F6"
                />
              </div>

              {redeemMessage && (
                <div
                  role={redeemMessageType === 'error' ? 'alert' : 'status'}
                  className={
                    redeemMessageType === 'error'
                      ? 'bg-danger-bg text-danger-fg px-4 py-2 rounded-field'
                      : 'bg-success-bg text-success-fg px-4 py-2 rounded-field'
                  }
                >
                  {redeemMessage}
                </div>
              )}

              <Button
                type="submit"
                loading={redeemCode.isPending}
                disabled={!codeInput.trim() || redeemCode.isPending}
              >
                {redeemCode.isPending ? '引き換え中...' : '引き換える'}
              </Button>
            </form>
          </div>

          <details className="pt-6 border-t">
            <summary className="cursor-pointer text-base font-medium">
              特典コードのご利用条件
            </summary>
            <div className="mt-3">
              <RedemptionTerms />
            </div>
          </details>
        </Card>

        {/* ログアウトは「危険」ではなく日常操作。赤のベタ塗りはアカウント削除だけに残し、
            画面内の視覚的な優先順位を保つ（誤操作は確認モーダルで防ぐ） */}
        <Card className="mb-6">
          <Button variant="secondary" onClick={() => setShowLogoutConfirm(true)} className="w-full">
            ログアウト
          </Button>
        </Card>

        {/* アカウント削除（退会）— 誤操作防止のためページ最下部に配置 */}
        <Card className="border-2 border-danger/30 bg-danger-bg">
          <h2 className="text-xl font-bold text-danger-fg mb-4 flex items-center gap-2">
            <Trash2 className="w-5 h-5" />
            アカウント削除
          </h2>
          <p className="text-gray-700 mb-4">
            アカウントを削除すると、以下のデータがすべて完全に削除されます：
          </p>
          <ul className="list-disc list-inside text-gray-600 mb-6 space-y-1">
            <li>プロフィール情報</li>
            <li>すべての書き起こしデータ</li>
            <li>プラン・課金情報</li>
          </ul>
          <p className="text-danger-fg text-sm mb-4">
            ※ この操作は取り消せません
          </p>
          <Button variant="danger" onClick={() => setShowDeleteModal(true)}>
            アカウントを削除する
          </Button>
        </Card>
      </main>

      {/* ログアウト確認 */}
      <ConfirmDialog
        open={showLogoutConfirm}
        title="ログアウトしますか？"
        message="再度ご利用いただくには、もう一度ログインが必要です。"
        confirmLabel="ログアウト"
        onConfirm={handleLogout}
        onClose={() => setShowLogoutConfirm(false)}
      />

      {/* アカウント削除モーダル */}
      <DeleteAccountModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteAccount}
      />
    </Layout>
  );
}
